import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell.jsx';
import { ClientChatWidget } from '../components/ClientChatWidget.jsx';
import { NotificationsBell } from '../components/NotificationsBell.jsx';
import { SidebarHelp, SidebarLink } from '../components/SidebarNav.jsx';
import { UserMenu } from '../components/UserMenu.jsx';
import { usePortalBranding } from '../branding/usePortalBranding.js';
import { NotificationSocketProvider } from '../context/NotificationSocketContext.jsx';
import { OnboardingHelpButton } from '../onboarding/OnboardingProvider.jsx';
import { useAuth } from '../context/AuthContext.jsx';

export function MyPortalLayout() {
  const { user } = useAuth();
  const { rootRef, branding } = usePortalBranding('/api/my/branding');
  const caps = user?.capabilities ?? {};
  const showFaq = caps.view_client_faq;
  const showProjects = caps.view_portal_projects;
  const showAssets = caps.view_client_assets;
  const showTasks = caps.view_client_tasks;
  const chatEnabled = caps.use_live_chat || branding?.chat_enabled;
  const chatColor = branding?.link_color || branding?.primary_color || '#4f46e5';
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <NotificationSocketProvider>
      <AppShell
        rootRef={rootRef}
        branding={branding}
        subtitle="Client Portal"
        searchPlaceholder="Search projects, files, or messages…"
        onSearch={(term) => {
          const q = encodeURIComponent(term);
          if (location.pathname.startsWith('/my/files')) {
            navigate(`/my/files?q=${q}`);
            return;
          }
          if (location.pathname.startsWith('/my/emails')) {
            navigate(`/my/emails?q=${q}`);
            return;
          }
          navigate(`/my/projects?q=${q}`);
        }}
        nav={
          <>
            <SidebarLink to="/my/dashboard" label="Home" end icon="home" tourId="tour-nav-dashboard" />
            {showProjects ? (
              <SidebarLink to="/my/projects" label="My Project" icon="projects" tourId="tour-nav-projects" />
            ) : null}
            {showProjects ? <SidebarLink to="/my/files" label="Files" icon="files" /> : null}
            {caps.view_client_emails ? (
              <SidebarLink to="/my/emails" label="Messages" icon="messages" tourId="tour-nav-communication" />
            ) : null}
            {caps.view_client_meetings ? (
              <SidebarLink to="/my/meetings" label="Meetings" icon="calendar" />
            ) : null}
            {caps.view_client_forms ? <SidebarLink to="/my/forms" label="Forms" icon="plans" tourId="tour-nav-my-work" /> : null}
            {showFaq ? <SidebarLink to="/my/faq" label="FAQ" icon="directory" /> : null}
            {showTasks ? <SidebarLink to="/my/tasks" label="My tasks" icon="work" /> : null}
            {showAssets ? <SidebarLink to="/my/assets" label="Media & assets" icon="files" /> : null}
          </>
        }
        topbar={
          <div className="topbar-utilities" data-tour="tour-utilities">
            <NotificationsBell />
            <OnboardingHelpButton />
            <UserMenu profileTo="/my/profile" roleLabel="Client" />
          </div>
        }
        footer={<SidebarHelp href={branding?.support_email ? `mailto:${branding.support_email}` : undefined} />}
      >
        <Outlet />
      </AppShell>
      <ClientChatWidget enabled={chatEnabled} primaryColor={chatColor} />
    </NotificationSocketProvider>
  );
}
